import { useState, useEffect } from 'react'
import { FiUsers, FiClock, FiCheck, FiAlertCircle, FiCoffee } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { useFichajeGrupal } from '../../hooks/useFichajeGrupal'
import { PageHeader, Btn } from '../../ui'

/*
 * FichajeGrupal (V2) — interfaz del manager para fichar a su equipo.
 * Selecciona grupo + fecha + tipo (entrada/salida/jornada), valida con preview
 * y confirma. El backend aplica regla "manual prevalece" por componente.
 */
const TIPOS = [
  { id: 'entrada', label: 'Fichar entrada',  desc: 'Solo entrada. Salida la pondrá cada empleado o tú al cerrar el día.' },
  { id: 'salida',  label: 'Fichar salida',   desc: 'Cierra los fichajes del día. Solo afecta a quien tenga entrada sin salida.' },
  { id: 'jornada', label: 'Fichar jornada',  desc: 'Entrada y salida en una sola operación. Útil para registrar el día anterior.' }
]

const FichajeGrupalV2 = () => {
  const { misGrupos, cargando: cargandoGrupos, getPreview, submit } = useFichajeGrupal()
  const [grupoId, setGrupoId] = useState(null)
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10))
  const [tipo, setTipo] = useState('entrada')
  const [horaEntrada, setHoraEntrada] = useState('')
  const [horaSalida, setHoraSalida] = useState('')
  const [aplicarPausa, setAplicarPausa] = useState(false)
  const [horaInicioPausa, setHoraInicioPausa] = useState('')
  const [duracionPausa, setDuracionPausa] = useState(30)
  const [excluidos, setExcluidos] = useState(new Set())
  const [preview, setPreview] = useState(null)
  const [cargandoPreview, setCargandoPreview] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [resultado, setResultado] = useState(null)

  useEffect(() => {
    if (!grupoId && misGrupos.length > 0) setGrupoId(misGrupos[0].id)
  }, [misGrupos, grupoId])

  useEffect(() => {
    if (!grupoId || !fecha) return
    setCargandoPreview(true)
    setResultado(null)
    getPreview(grupoId, fecha)
      .then((d) => {
        setPreview(d)
        setHoraEntrada(d?.defaults?.hora_entrada || '09:00')
        setHoraSalida(d?.defaults?.hora_salida || '18:00')
        setAplicarPausa(!!d?.defaults?.pausa_obligatoria)
        setHoraInicioPausa(d?.defaults?.hora_inicio_pausa || '13:30')
        setDuracionPausa(d?.defaults?.duracion_pausa_min || 30)
        setExcluidos(new Set())
      })
      .catch((e) => toast.error(e.message || 'Error cargando preview'))
      .finally(() => setCargandoPreview(false))
  }, [grupoId, fecha, getPreview])

  const toggleExcluido = (id) => {
    setExcluidos((prev) => {
      const s = new Set(prev)
      if (s.has(id)) s.delete(id); else s.add(id)
      return s
    })
  }

  const mostrarPausa = tipo === 'salida' || tipo === 'jornada'

  const onConfirmar = async () => {
    if (!grupoId) { toast.error('Selecciona un grupo'); return }
    setEnviando(true); setResultado(null)
    try {
      const payload = {
        grupo_id: grupoId, fecha, tipo,
        hora_entrada: horaEntrada,
        hora_salida: horaSalida,
        empleados_excluidos: Array.from(excluidos)
      }
      if (mostrarPausa) {
        payload.pausa = {
          aplicar: aplicarPausa,
          hora_inicio: horaInicioPausa,
          duracion_min: Number(duracionPausa) || 0
        }
      }
      const r = await submit(payload)
      setResultado(r)
      const total = (r.creados?.length || 0) + (r.parciales?.length || 0)
      toast.success(`Procesado: ${total} fichajes registrados, ${r.omitidos?.length || 0} omitidos`)
      // Recargar preview para ver el nuevo estado
      const newPrev = await getPreview(grupoId, fecha)
      setPreview(newPrev)
    } catch (e) {
      toast.error(e.message || 'Error al procesar')
    } finally {
      setEnviando(false)
    }
  }

  const grupoActual = misGrupos.find((g) => g.id === grupoId)

  if (cargandoGrupos) {
    return (
      <div className="px-6 py-6 max-w-7xl mx-auto">
        <PageHeader num="§ 05" kicker="Fichaje grupal" title="Fichaje del" italic="equipo" />
        <div className="ui-card p-6 mt-6">
          <p className="text-[13px] text-[var(--ink-3)]">Cargando…</p>
        </div>
      </div>
    )
  }

  if (!misGrupos.length) {
    return (
      <div className="px-6 py-6 max-w-7xl mx-auto">
        <PageHeader num="§ 05" kicker="Fichaje grupal" title="Fichaje del" italic="equipo" />
        <div className="ui-card p-6 mt-6">
          <p className="text-[13px] text-[var(--ink-2)]">No eres manager de ningún grupo todavía. Pide al admin que te asigne uno.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="px-6 py-6 max-w-7xl mx-auto space-y-5">
      <PageHeader
        num="§ 05"
        kicker="Fichaje grupal"
        title="Fichaje del"
        italic="equipo"
        sub="Registra entradas, salidas o jornadas completas para los empleados de tus grupos."
      />

      {/* Toolbar */}
      <div className="ui-card p-4 flex flex-wrap gap-3 items-center">
        <div>
          <label className="eyebrow block mb-1">Grupo</label>
          <select
            value={grupoId || ''}
            onChange={(e) => setGrupoId(parseInt(e.target.value))}
            className="px-3 py-1.5 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[13px] min-w-[220px]"
          >
            {misGrupos.map((g) => (
              <option key={g.id} value={g.id}>{g.nombre} ({g.num_empleados})</option>
            ))}
          </select>
        </div>
        <div>
          <label className="eyebrow block mb-1">Fecha</label>
          <input
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            className="px-3 py-1.5 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[13px]"
          />
        </div>
        {grupoActual && (
          <div className="ml-auto text-[11px] text-[var(--ink-3)]">
            Calendario: <b className="text-[var(--ink-2)]">{grupoActual.calendario_nombre || '—'}</b>
            {grupoActual.centro_nombre && <> · Centro: <b className="text-[var(--ink-2)]">{grupoActual.centro_nombre}</b></>}
          </div>
        )}
      </div>

      {/* Tipo */}
      <div className="ui-card p-4">
        <label className="eyebrow block mb-2">Acción</label>
        <div className="grid sm:grid-cols-3 gap-2">
          {TIPOS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTipo(t.id)}
              className={`p-3 text-left rounded-soft border transition ${
                tipo === t.id
                  ? 'border-[var(--brand-1)] bg-[rgba(37,99,235,.06)]'
                  : 'border-[var(--line)] hover:bg-[var(--bg-2)]'
              }`}
            >
              <div className="text-[13px] font-medium text-[var(--ink)]">{t.label}</div>
              <div className="text-[11px] text-[var(--ink-3)] mt-1">{t.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Horas */}
      <div className="ui-card p-4 grid sm:grid-cols-2 gap-4">
        {(tipo === 'entrada' || tipo === 'jornada') && (
          <div>
            <label className="eyebrow block mb-1.5 flex items-center gap-1">
              <FiClock size={11} /> Hora de entrada
            </label>
            <input
              type="time"
              value={horaEntrada}
              onChange={(e) => setHoraEntrada(e.target.value)}
              className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px]"
            />
            <p className="text-[11px] text-[var(--ink-3)] mt-1">Default del calendario, editable.</p>
          </div>
        )}
        {(tipo === 'salida' || tipo === 'jornada') && (
          <div>
            <label className="eyebrow block mb-1.5 flex items-center gap-1">
              <FiClock size={11} /> Hora de salida
            </label>
            <input
              type="time"
              value={horaSalida}
              onChange={(e) => setHoraSalida(e.target.value)}
              className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px]"
            />
          </div>
        )}
      </div>

      {/* Pausa */}
      {mostrarPausa && (
        <div className="ui-card p-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={aplicarPausa}
              onChange={(e) => setAplicarPausa(e.target.checked)}
              className="cursor-pointer"
            />
            <span className="font-display text-[14px] text-[var(--ink)] flex items-center gap-2">
              <FiCoffee size={13} className="text-[var(--ink-2)]" /> Aplicar pausa
              {preview?.defaults?.pausa_obligatoria && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(168,106,6,.10)] text-[#A86A06]">
                  Obligatoria por calendario
                </span>
              )}
            </span>
          </label>
          <p className="text-[11px] text-[var(--ink-3)] mt-1.5">
            La pausa se inserta solo si el empleado no ha registrado pausas manualmente. Se descuenta del tiempo total.
          </p>
          {aplicarPausa && (
            <div className="grid sm:grid-cols-2 gap-4 mt-3">
              <div>
                <label className="eyebrow block mb-1.5">Hora de inicio</label>
                <input
                  type="time"
                  value={horaInicioPausa}
                  onChange={(e) => setHoraInicioPausa(e.target.value)}
                  className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px]"
                />
              </div>
              <div>
                <label className="eyebrow block mb-1.5">Duración (min)</label>
                <input
                  type="number"
                  min="1"
                  max="480"
                  value={duracionPausa}
                  onChange={(e) => setDuracionPausa(e.target.value)}
                  className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px]"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Roster */}
      <div className="ui-card p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-[16px] text-[var(--ink)] flex items-center gap-2">
            <FiUsers size={14} className="text-[var(--ink-2)]" /> Empleados del grupo
          </h3>
          <span className="text-[11px] text-[var(--ink-3)]">{preview?.empleados?.length || 0} miembros</span>
        </div>
        {cargandoPreview ? (
          <p className="text-[12px] text-[var(--ink-3)]">Cargando…</p>
        ) : preview?.empleados?.length === 0 ? (
          <p className="text-[12px] text-[var(--ink-3)]">El grupo no tiene empleados activos.</p>
        ) : (
          <div className="space-y-2">
            {preview?.empleados?.map((e) => (
              <div key={e.empleado_id} className={`flex items-center gap-3 p-2 rounded-soft border ${
                excluidos.has(e.empleado_id) ? 'bg-[var(--bg-2)] border-[var(--line)] opacity-60' : 'border-[var(--line)]'
              }`}>
                <input
                  type="checkbox"
                  checked={!excluidos.has(e.empleado_id)}
                  onChange={() => toggleExcluido(e.empleado_id)}
                  className="cursor-pointer"
                />
                <div className="flex-1">
                  <div className="text-[13px] text-[var(--ink)]">{e.nombre}</div>
                  <div className="text-[11px] text-[var(--ink-3)]">{e.email}</div>
                </div>
                <EstadoFichaje empleado={e} tipo={tipo} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Acción */}
      <div className="ui-card p-4 flex justify-end">
        <Btn variant="primary" onClick={onConfirmar} disabled={enviando || !preview || (preview?.empleados?.length === 0)}>
          {enviando ? 'Procesando…' : 'Confirmar fichaje grupal'}
        </Btn>
      </div>

      {/* Resultado */}
      {resultado && (
        <div className="ui-card p-5 space-y-3">
          <h3 className="font-display text-[16px] text-[var(--ink)]">Resultado</h3>
          <ResultadoBloque label="Creados" items={resultado.creados} color="#0F8C66" Icon={FiCheck} />
          <ResultadoBloque label="Parciales (solo añadido)" items={resultado.parciales} color="#2563EB" Icon={FiCheck} />
          <ResultadoBloque label="Omitidos" items={resultado.omitidos} color="#A86A06" Icon={FiAlertCircle} mostrarMotivo />
        </div>
      )}
    </div>
  )
}

const EstadoFichaje = ({ empleado, tipo }) => {
  const f = empleado.fichaje_existente
  if (empleado.ausencia) {
    return <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(168,106,6,.10)] text-[#A86A06]">Ausencia: {empleado.ausencia.tipo}</span>
  }
  if (!f) {
    return <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--bg-2)] text-[var(--ink-3)]">Sin fichaje</span>
  }
  const tieneEntrada = !!f.hora_entrada
  const tieneSalida = !!f.hora_salida
  const origenEnt = f.origen_entrada || 'manual'
  const origenSal = f.origen_salida
  if (tieneEntrada && tieneSalida) {
    return <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(15,140,102,.10)] text-[#0F8C66]">
      {f.hora_entrada}-{f.hora_salida} {origenEnt === 'manual' || origenSal === 'manual' ? '(manual)' : '(grupal)'}
    </span>
  }
  if (tieneEntrada && !tieneSalida) {
    return <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(37,99,235,.10)] text-[var(--brand-1)]">
      Solo entrada {f.hora_entrada} {origenEnt === 'manual' ? '(manual)' : '(grupal)'}
    </span>
  }
  return <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--bg-2)] text-[var(--ink-3)]">Sin fichaje</span>
}

const ResultadoBloque = ({ label, items, color, Icon, mostrarMotivo }) => {
  if (!items || items.length === 0) return null
  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={12} style={{ color }} />
        <span className="text-[11px] uppercase tracking-wide text-[var(--ink-2)]">{label} ({items.length})</span>
      </div>
      <div className="space-y-1">
        {items.map((it, i) => (
          <div key={i} className="text-[12px] text-[var(--ink-2)] pl-5">
            {it.nombre}{mostrarMotivo && it.motivo ? ` — ${it.motivo}` : ''}{it.componente ? ` · ${it.componente}` : ''}
          </div>
        ))}
      </div>
    </div>
  )
}

export default FichajeGrupalV2
